import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, FileText, Clock, AlertCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import GuideBreadcrumb from "@/components/GuideBreadcrumb";
import LanguageSelector from "@/components/LanguageSelector";

export default function BandiVsPreinformazione() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Differenza tra Bandi di Gara e Avvisi di Pre-informazione"
        description="Scopri la differenza tra bandi di gara e avvisi di pre-informazione negli appalti europei. Guida pratica per comprendere le pubblicazioni TED."
        path="/it/guide/bandi-vs-preinformazione"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/guide/bandi-vs-preinformazione" },
          { lang: "x-default", href: "https://tedmonitor.eu/it/guide/bandi-vs-preinformazione" },
        ]}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", href: "https://tedmonitor.eu/" },
          { name: "Guide", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
          { name: "Bandi vs Pre-informazione", href: "https://tedmonitor.eu/it/guide/bandi-vs-preinformazione" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <GuideBreadcrumb
          items={[
            { label: "Guide", href: "/it/guide/consultare-ted" },
            { label: "Bandi vs Pre-informazione" },
          ]}
        />
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-4">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Guida
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Differenza tra Bandi di Gara e Avvisi di Pre-informazione
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Nel mondo degli appalti pubblici europei, esistono diversi tipi di pubblicazioni sul TED. Comprendere la
              differenza tra bandi di gara e avvisi di pre-informazione è essenziale per pianificare la tua strategia
              commerciale.
            </p>

            {/* Internal links */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/guide/consultare-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Come consultare il TED
              </Link>
              <Link
                to="/it/guide/codici-cpv"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Guida ai Codici CPV
              </Link>
              <Link
                to="/it/guide/filtri-ricerca-avanzata"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Filtri di ricerca avanzata
              </Link>
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio appalti europei
              </Link>
            </div>
          </div>
        </section>

        {/* Bandi di gara */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <FileText className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cosa sono i bandi di gara</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              I <strong>bandi di gara</strong> (contract notices) sono avvisi pubblicati quando una stazione appaltante
              intende aggiudicare un contratto. Rappresentano opportunità concrete e immediate.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Caratteristiche principali:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Contengono una scadenza precisa per la presentazione delle offerte",
                "Includono tutti i requisiti di partecipazione",
                "Specificano l'oggetto del contratto e il valore stimato",
                "Indicano la procedura di gara (aperta, ristretta, negoziata, ecc.)",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Azione richiesta:</strong> Preparare e presentare un'offerta entro la scadenza indicata.
              </p>
            </div>
          </div>
        </section>

        {/* Avvisi di pre-informazione */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Clock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Cosa sono gli avvisi di pre-informazione
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Gli <strong>avvisi di pre-informazione</strong> (prior information notices o PIN) sono pubblicazioni
              anticipate che informano il mercato di future intenzioni di acquisto. Non sono ancora gare vere e proprie.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Caratteristiche principali:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Annunciano gare che verranno bandite nei mesi successivi",
                "Non hanno una scadenza per presentare offerte",
                "Forniscono una stima indicativa di budget e tempistiche",
                "Permettono di ridurre i termini minimi nel successivo bando",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Azione consigliata:</strong> Prepararsi in anticipo, studiare l'ente appaltante, valutare
                partnership.
              </p>
            </div>
          </div>
        </section>

        {/* Confronto */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Confronto diretto</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-lg">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-3 font-medium">Aspetto</th>
                    <th className="text-left p-3 font-medium">Bando di gara</th>
                    <th className="text-left p-3 font-medium">Pre-informazione</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="p-3 text-muted-foreground">Scadenza</td>
                    <td className="p-3">Sì, precisa</td>
                    <td className="p-3">No</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-muted-foreground">Possibilità di partecipare</td>
                    <td className="p-3">Immediata</td>
                    <td className="p-3">Futura</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-muted-foreground">Documentazione completa</td>
                    <td className="p-3">Sì</td>
                    <td className="p-3">Parziale</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-muted-foreground">Valore strategico</td>
                    <td className="p-3">Operativo</td>
                    <td className="p-3">Pianificazione</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Perché monitorare entrambi */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <BookOpen className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Perché monitorare entrambi</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Una strategia di procurement efficace richiede di monitorare sia i bandi di gara che gli avvisi di
              pre-informazione:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "I PIN ti danno tempo per prepararti prima che esca il bando ufficiale",
                "Puoi costruire partnership e team di progetto con anticipo",
                "Conosci i budget stimati per valutare se partecipare",
                "I bandi ti permettono di agire immediatamente sulle opportunità",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor ti permette di configurare alert per entrambi i tipi di pubblicazione, così non perdi nessuna
              opportunità.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Vuoi ricevere alert su bandi e pre-informazioni?
            </h2>
            <p className="text-base mb-6 opacity-90">
              TED Monitor ti permette di monitorare tutti i tipi di pubblicazione TED e ricevere notifiche
              personalizzate.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/fonte-dei-dati" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
